import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PiFileZip, PiReceipt, PiSpinnerGap, PiWarning } from "react-icons/pi";
import "./AccessEnded.css";
import { supabase } from "../SupabaseClient.tsx";
import ActionButton from "../components/ActionButton.tsx";
import InfoBanner from "../components/InfoBanner.tsx";
import TileMetric from "../components/TileMetric.tsx";
import { getRenewalAction, Lapse, LapseReason } from "../utils/checkout.ts";
import { exportReceipts, fetchLibraryOrders } from "../utils/exportReceipts.ts";

const TITLES: Record<LapseReason, string> = {
  trial_ended: "Your free trial has ended",
  payment_failed: "We couldn't process your payment",
  inactive: "Your subscription isn't active",
};

const SUBSCRIPTION_FEATURES = [
  "Upload receipts in the app or by email",
  "Edit receipts and confirm email uploads",
  "Track your reimbursements",
  "Project your savings",
];

// The home page for someone whose trial or subscription has ended. They stay signed in with
// read-only access to their receipts, and this page shows how to get full access back.
const AccessEnded = ({ lapse }: { lapse: Lapse }) => {
  const navigate = useNavigate();
  const [receiptCount, setReceiptCount] = useState<number | null>(null);
  const [availableFunds, setAvailableFunds] = useState<number>(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const renewal = getRenewalAction(lapse);

  useEffect(() => {
    const fetchSummary = async () => {
      const { data, error } = await supabase
        .from("view_reimburse_amount")
        .select("reimbursable_amount");

      if (error) {
        console.error("Error fetching receipt summary:", error);
        return;
      }

      setReceiptCount(data.length);
      setAvailableFunds(
        data.reduce((sum, order) => sum + (order.reimbursable_amount || 0), 0)
      );
    };

    fetchSummary();
  }, []);

  const handleDownload = async () => {
    setExporting(true);
    setExportError(null);
    const orders = await fetchLibraryOrders();
    setExportError(
      orders
        ? await exportReceipts(orders)
        : "We couldn't load your receipts. Please try again, or contact support@shoebox.io."
    );
    setExporting(false);
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>{TITLES[lapse.reason]}</h1>
            <p>
              {lapse.reason === "payment_failed"
                ? "Your account is read-only until your payment goes through. "
                : ""}
              Your receipts are safe, and you can still view and download them.
              {lapse.reason === "trial_ended" &&
                " You won't be charged unless you subscribe."}
            </p>
          </div>
        </div>

        {exportError && (
          <InfoBanner icon={<PiWarning size={24} />} color="red">
            <p>{exportError}</p>
          </InfoBanner>
        )}

        {receiptCount !== null && receiptCount > 0 && (
          <>
            <div className="access-ended-metrics">
              <TileMetric
                title="Receipts Saved"
                value={receiptCount.toString()}
                text="Kept in your account, ready to view or download."
              />
              <TileMetric
                title="Available Funds"
                value={`$${new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(availableFunds)}`}
                text="Available for tax-free withdrawal anytime."
                color="var(--color-green-600)"
              />
            </div>
            <div className="access-ended-actions">
              <ActionButton
                icon={<PiReceipt />}
                text="View Receipts"
                onClick={() => navigate("/receipt-library")}
                secondary={true}
              />
              <ActionButton
                icon={
                  exporting ? <PiSpinnerGap className="spin" /> : <PiFileZip />
                }
                text="Download All"
                onClick={handleDownload}
                secondary={true}
                disabled={exporting}
              />
            </div>
          </>
        )}

        <div className="access-ended-renew">
          <h2>Pick up where you left off</h2>
          <ul>
            {SUBSCRIPTION_FEATURES.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
          <ActionButton
            text={renewal.text}
            onClick={() => {
              window.location.href = renewal.url;
            }}
          />
          <p className="access-ended-support">
            Questions? Email us at support@shoebox.io.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccessEnded;
